import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiUser, FiCalendar, FiDollarSign, FiTrendingDown, FiGift, FiMinus, FiFileText, FiStar, FiTrendingUp, FiTrash2, FiClock } from "react-icons/fi";
import {
  getUsers,
  getAllPayrolls,
  getPayrollByUserId,
  createPayroll,
  updatePayroll,
  deletePayroll,
} from "../api/api";
import toast from "react-hot-toast";
import FieldToast from "../components/FieldToast";
import "./Payroll.css";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const MONTHS = [
  { value: 1,  label: "January" },  { value: 2,  label: "February" },
  { value: 3,  label: "March" },    { value: 4,  label: "April" },
  { value: 5,  label: "May" },      { value: 6,  label: "June" },
  { value: 7,  label: "July" },     { value: 8,  label: "August" },
  { value: 9,  label: "September" },{ value: 10, label: "October" },
  { value: 11, label: "November" },{ value: 12, label: "December" },
];
const monthLabel = (m) => MONTHS.find(x => x.value === Number(m))?.label || "—";

const now = new Date();
const currentMonthNum = now.getMonth() + 1;
const currentYear = now.getFullYear();

const DEPT_COLORS = {
  Software:  { color: "#60a5fa", bg: "#0d1f3c" },
  Marketing: { color: "#f472b6", bg: "#2d0a1e" },
  CEO:       { color: "#facc15", bg: "#1e1500" },
  COO:       { color: "#fb923c", bg: "#1e0e00" },
  HR:        { color: "#a78bfa", bg: "#1a1040" },
  Other:     { color: "#94a3b8", bg: "#1a2540" },
};

function getInitials(name = "") {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

// Mirrors the server-side formula from the integration guide, used only for
// an instant client-side preview — the real numbers always come from the API.
function calculate({ basicSalary = 0, absences = 0, bonus = 0, manualDeduction = 0 }) {
  const base = Number(basicSalary) || 0;
  const abs  = Number(absences) || 0;
  const bon  = Number(bonus) || 0;
  const ded  = Number(manualDeduction) || 0;
  const dailyRate = base / 30;
  const absenceDeduction = abs * dailyRate;
  const totalDeduction = absenceDeduction + ded;
  const netSalary = base + bon - totalDeduction;
  return {
    dailyRate: +dailyRate.toFixed(2),
    absenceDeduction: +absenceDeduction.toFixed(2),
    totalDeduction: +totalDeduction.toFixed(2),
    netSalary: +netSalary.toFixed(2),
  };
}

const emptyPayrollForm = (month = currentMonthNum, year = currentYear) => ({
  userId: "", month, year, basicSalary: "", absences: "", bonus: "", manualDeduction: "", notes: "",
});

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
export default function Payroll() {
  const navigate = useNavigate();

  const [payrolls, setPayrolls] = useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  const [monthFilter, setMonthFilter] = useState("All");
  const [search,      setSearch]      = useState("");
  const [deptFilter,  setDeptFilter]  = useState("All");

  const [showAddModal,    setShowAddModal]    = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal,setShowHistoryModal]= useState(false);

  const [addForm,  setAddForm]  = useState(emptyPayrollForm());
  const [editForm, setEditForm] = useState(null);
  const [adding,   setAdding]   = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const [editingPayroll,  setEditingPayroll]  = useState(null);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [historyPayroll,  setHistoryPayroll]  = useState(null);

  const userRef      = useRef(null);
  const monthRef     = useRef(null);
  const yearRef      = useRef(null);
  const salaryRef    = useRef(null);
  const [fieldToast, setFieldToast] = useState({ show: false, message: "", targetRef: null });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({ show: true, message, targetRef });
    setTimeout(() => setFieldToast(prev => ({ ...prev, show: false })), 3000);
  };

  // ── Fetch ────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, payrollsData] = await Promise.all([getUsers(), getAllPayrolls(1, 500)]);
      
      const usersList    = Array.isArray(usersData?.data) ? usersData.data : (Array.isArray(usersData) ? usersData : []);
      const payrollsList = Array.isArray(payrollsData?.data?.data) ? payrollsData.data.data : (Array.isArray(payrollsData?.data) ? payrollsData.data : (Array.isArray(payrollsData) ? payrollsData : []));
      
      setUsers(usersList);
      setPayrolls(payrollsList);
    } catch (err) {
      toast.error(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Enrich ───────────────────────────────────
  const enriched = useMemo(() => payrolls.map(payroll => {
    const user = users.find(u => u._id === (payroll.userId?._id || payroll.userId));
    const calc = calculate(payroll);
    return {
      _id:             payroll._id,
      userId:          payroll.userId?._id || payroll.userId,
      basicSalary:     payroll.basicSalary || 0,
      absences:        payroll.absences    || 0,
      bonus:           payroll.bonus       || 0,
      manualDeduction: payroll.manualDeduction || 0,
      month:           payroll.month,
      year:            payroll.year,
      notes:           payroll.notes || "",
      // Prefer server-computed values; fall back to the client formula.
      dailyRate:        payroll.dailyRate        ?? calc.dailyRate,
      absenceDeduction: payroll.absenceDeduction  ?? calc.absenceDeduction,
      totalDeduction:   payroll.totalDeduction    ?? calc.totalDeduction,
      netSalary:        payroll.netSalary         ?? calc.netSalary,
      displayName:   user?.userName  || user?.name  || "Unknown",
      displayRole:   user?.position  || user?.role  || "—",
      displayDept:   user?.department || "Other",
      displayAvatar: getInitials(user?.userName || user?.name || "?"),
    };
  }), [payrolls, users]);

  // ── Stats (scoped to whatever the filters currently show) ──
  const departments = ["All", ...Array.from(new Set(enriched.map(p => p.displayDept)))];

  const filtered = enriched
    .filter(p => deptFilter === "All" || p.displayDept === deptFilter)
    .filter(p => monthFilter === "All" || Number(p.month) === Number(monthFilter))
    .filter(p => p.displayName.toLowerCase().includes(search.toLowerCase()));

  const totalPayroll    = filtered.reduce((s, p) => s + p.netSalary, 0);
  const totalBonuses    = filtered.reduce((s, p) => s + p.bonus, 0);
  const totalDeductions = filtered.reduce((s, p) => s + p.totalDeduction, 0);

  // ── Add: pre-fill basic salary from the employee's last payroll ──
  const handleSelectEmployee = async (userId) => {
    setAddForm(prev => ({ ...prev, userId }));
    if (!userId) return;
    try {
      const res = await getPayrollByUserId(userId);
      const data = res?.data ?? res;
      const last = Array.isArray(data) ? data[data.length - 1] : data;
      if (last?.basicSalary) {
        setAddForm(prev => ({ ...prev, basicSalary: last.basicSalary }));
      }
    } catch {
      // No prior record for this employee — nothing to pre-fill, that's fine.
    }
  };

  const conflictMessage = (month, year) =>
    `تم إنشاء كشف المرتب لهذا الموظف بالفعل لشهر ${month} / ${year} — يمكنك تعديله من قائمة المرتبات`;

  // ── Add ──────────────────────────────────────
  const handleAdd = async () => {
    const errors = {};
    
    if (!addForm.userId) {
      errors.userId = "Please select an employee";
    }
    if (!addForm.month) {
      errors.month = "Please select a month";
    }
    if (!addForm.year) {
      errors.year = "Please select a year";
    }
    if (!addForm.basicSalary || Number(addForm.basicSalary) <= 0) {
      errors.basicSalary = "Please enter a valid base salary";
    } else if (Number(addForm.basicSalary) > 1000000) {
      errors.basicSalary = "Base salary cannot exceed 1,000,000";
    }
    const absencesNum = addForm.absences === "" ? 0 : Number(addForm.absences);
    if (absencesNum < 0 || absencesNum > 30) {
      errors.absences = "Absences cannot exceed 30 days per month";
    }
    const bonusNum = addForm.bonus ? Number(addForm.bonus) : 0;
    if (bonusNum < 0) {
      errors.bonus = "Bonus cannot be negative";
    } else if (bonusNum > Number(addForm.basicSalary)) {
      errors.bonus = "Bonus cannot exceed base salary";
    }

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setAddErrors({});

    const payload = {
      userId: addForm.userId,
      month:  Number(addForm.month),
      year:   Number(addForm.year),
      basicSalary: Number(addForm.basicSalary),
      absences: absencesNum,
      bonus: addForm.bonus ? Number(addForm.bonus) : 0,
      manualDeduction: addForm.manualDeduction ? Number(addForm.manualDeduction) : 0,
      notes: addForm.notes || null,
    };

    try {
      setAdding(true);
      const res = await createPayroll(payload);
      const created = res?.data ?? res;
      setPayrolls(prev => [...prev, created]);
      toast.success("Employee added to payroll");
      setShowAddModal(false);
      setAddForm(emptyPayrollForm());
      setAddErrors({});
    } catch (err) {
      const status = err?.status || err?.response?.status;
      if (status === 409) {
        toast.error(conflictMessage(payload.month, payload.year));
      } else {
        toast.error(err?.message || err?.response?.data?.message || "Failed to add employee");
      }
    } finally {
      setAdding(false);
    }
  };

  // ── Delete ───────────────────────────────────
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this payroll record?")) return;
    try {
      await deletePayroll(id);
      setPayrolls(prev => prev.filter(p => p._id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.message || "Failed to delete");
    }
  };

  // ── Edit ─────────────────────────────────────
  const handleEdit = (payroll, e) => {
    e.stopPropagation();
    setEditingPayroll(payroll);
    setEditForm({
      month: payroll.month,
      year: payroll.year,
      basicSalary: payroll.basicSalary,
      absences: payroll.absences,
      bonus: payroll.bonus,
      manualDeduction: payroll.manualDeduction,
      notes: payroll.notes || "",
    });
    setShowEditModal(true);
  };

  // ── Update ───────────────────────────────────
  const handleUpdate = async () => {
    if (!editForm) return;
    const errors = {};
    
    const absencesNum = editForm.absences === "" ? 0 : Number(editForm.absences);
    if (absencesNum < 0 || absencesNum > 30) {
      errors.absences = "Absences cannot exceed 30 days per month";
    }
    const bonusNum = editForm.bonus ? Number(editForm.bonus) : 0;
    if (bonusNum < 0) {
      errors.bonus = "Bonus cannot be negative";
    } else if (bonusNum > Number(editForm.basicSalary)) {
      errors.bonus = "Bonus cannot exceed base salary";
    }

    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setEditErrors({});
    try {
      setEditing(true);
      const payload = {
        month: Number(editForm.month),
        year: Number(editForm.year),
        basicSalary: Number(editForm.basicSalary) || 0,
        absences: absencesNum,
        bonus: editForm.bonus ? Number(editForm.bonus) : 0,
        manualDeduction: editForm.manualDeduction ? Number(editForm.manualDeduction) : 0,
        notes: editForm.notes || null,
      };
      console.log("Updating payroll with ID:", editingPayroll._id);
      console.log("Payload:", payload);
      await updatePayroll(editingPayroll._id, payload);
      toast.success("Payroll updated successfully");
      setShowEditModal(false);
      setEditingPayroll(null);
      setEditForm(null);
      setEditErrors({});
      // Refetch data to ensure table and history are updated correctly
      fetchData();
    } catch (err) {
      console.error("Update payroll error:", err);
      const status = err?.status || err?.response?.status;
      if (status === 409) {
        toast.error(conflictMessage(editForm.month, editForm.year));
      } else {
        toast.error(err?.message || err?.response?.data?.message || "Failed to update payroll");
      }
    } finally {
      setEditing(false);
    }
  };

  const addPreview  = calculate(addForm);
  const editPreview = editForm ? calculate(editForm) : null;

  // History: every real record we already fetched for this employee, across
  // months and years — no client-side faking needed now that year is real.
  const historyEntries = historyPayroll
    ? enriched
        .filter(p => p.userId === historyPayroll.userId)
        .sort((a, b) => (a.year - b.year) || (a.month - b.month))
    : [];

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  if (loading) return <div className="payroll-loading">Loading...</div>;

  return (
    <div className="payroll-page">

      {/* ── Header ── */}
      <div className="payroll-header">
        <div>
          <h1 className="payroll-title">Payroll</h1>
          <p className="payroll-subtitle">Manage salaries, absences &amp; bonuses</p>
        </div>
        <div className="payroll-header-actions">
          <button
            className="add-emp-btn"
            onClick={() => {
              setAddForm(emptyPayrollForm(
                monthFilter !== "All" ? Number(monthFilter) : currentMonthNum,
                currentYear
              ));
              setShowAddModal(true);
            }}
          >
            + Add Employee
          </button>
          <button className="add-emp-btn" onClick={() => navigate("/finance/deleted")}>
            🗑️ Deleted
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="payroll-stats">
        {[
          { label: "Total Payroll (net)", value: totalPayroll.toLocaleString()    + " EGP", color: "#facc15", icon: <FiDollarSign size={24} /> },
          { label: "Total Bonuses",       value: "+" + totalBonuses.toLocaleString()    + " EGP", color: "#34d399", icon: <FiStar size={24} /> },
          { label: "Total Deductions",    value: "-" + totalDeductions.toLocaleString() + " EGP", color: "#f87171", icon: <FiTrendingDown size={24} /> },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="payroll-filters">
        <input
          className="payroll-search"
          placeholder="Search employees…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="month-year-select" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
          <option value="All">All Months</option>
          {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <div className="dept-filters">
          {departments.map(d => (
            <button
              key={d}
              className={`dept-btn ${deptFilter === d ? "active" : ""}`}
              onClick={() => setDeptFilter(d)}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="payroll-table-wrap">
        <table className="payroll-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Month</th>
              <th>Base Salary</th>
              <th>Absences</th>
              <th>Deductions</th>
              <th>Bonuses</th>
              <th>Net Salary</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const dept = DEPT_COLORS[p.displayDept] || { color: "#94a3b8", bg: "#1a2540" };
              return (
                <tr key={p._id} className="emp-row" onClick={() => { setSelectedPayroll(p); setShowDetailModal(true); }}>
                  <td>
                    <div className="emp-cell">
                      <div className="emp-avatar">{p.displayAvatar}</div>
                      <div>
                        <div className="emp-name">{p.displayName}</div>
                        <div className="emp-role">{p.displayRole}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="dept-badge" style={{ color: dept.color, background: dept.bg }}>
                      {p.displayDept}
                    </span>
                  </td>
                  <td>{monthLabel(p.month)} {p.year}</td>
                  <td className="num-cell">{p.basicSalary.toLocaleString()} EGP</td>
                  <td className="num-cell">{p.absences || "—"}</td>
                  <td className="num-cell red">
                    {p.totalDeduction > 0 ? "-" + p.totalDeduction.toLocaleString() + " EGP" : "—"}
                  </td>
                  <td className="num-cell green">
                    {p.bonus > 0 ? "+" + p.bonus.toLocaleString() + " EGP" : "—"}
                  </td>
                  <td className="num-cell net">{p.netSalary.toLocaleString()} EGP</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="history-btn"
                        onClick={e => { e.stopPropagation(); setHistoryPayroll(p); setShowHistoryModal(true); }}
                        title="History"
                      ><FiClock size={24} /></button>
                      <button className="edit-btn" onClick={e => handleEdit(p, e)} title="Edit">✎</button>
                      <button className="del-btn"  onClick={e => handleDelete(p._id, e)} title="Delete"><FiTrash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="empty-row">No employees found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add Employee Modal ── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Employee to Payroll</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label><FiUser size={16} style={{ marginRight: 8 }} /> Select Employee</label>
                <select 
                  ref={userRef} 
                  value={addForm.userId} 
                  onChange={e => { handleSelectEmployee(e.target.value); setAddErrors(prev => ({ ...prev, userId: '' })); }}
                  className={addErrors.userId ? 'error' : ''}
                >
                  <option value="">— Choose an employee —</option>
                  {users.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.userName || u.name} — {u.department || u.position || ""}
                    </option>
                  ))}
                </select>
                {addErrors.userId && <div className="field-error">{addErrors.userId}</div>}
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div className="modal-field" style={{ flex: 1 }}>
                  <label><FiCalendar size={16} style={{ marginRight: 8 }} /> Month</label>
                  <select 
                    ref={monthRef} 
                    value={addForm.month} 
                    onChange={e => { setAddForm({ ...addForm, month: Number(e.target.value) }); setAddErrors(prev => ({ ...prev, month: '' })); }}
                    className={addErrors.month ? 'error' : ''}
                  >
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  {addErrors.month && <div className="field-error">{addErrors.month}</div>}
                </div>
                <div className="modal-field" style={{ flex: 1 }}>
                  <label><FiCalendar size={16} style={{ marginRight: 8 }} /> Year</label>
                  <input 
                    ref={yearRef} 
                    type="number" 
                    value={addForm.year} 
                    onChange={e => { setAddForm({ ...addForm, year: e.target.value }); setAddErrors(prev => ({ ...prev, year: '' })); }}
                    className={addErrors.year ? 'error' : ''}
                  />
                  {addErrors.year && <div className="field-error">{addErrors.year}</div>}
                </div>
              </div>
              <div className="modal-field">
                <label><FiDollarSign size={16} style={{ marginRight: 8 }} /> Base Salary (EGP)</label>
                <input 
                  ref={salaryRef} 
                  type="number" 
                  value={addForm.basicSalary} 
                  onChange={e => { setAddForm({ ...addForm, basicSalary: e.target.value }); setAddErrors(prev => ({ ...prev, basicSalary: '' })); }}
                  placeholder="Enter salary amount"
                  className={addErrors.basicSalary ? 'error' : ''}
                />
                {addErrors.basicSalary && <div className="field-error">{addErrors.basicSalary}</div>}
              </div>
              <div className="modal-field">
                <label><FiTrendingDown size={16} style={{ marginRight: 8 }} /> Absences (days)</label>
                <input 
                  type="number" 
                  step="0.5" 
                  min="0" 
                  max="30" 
                  value={addForm.absences} 
                  onChange={e => { setAddForm({ ...addForm, absences: e.target.value }); setAddErrors(prev => ({ ...prev, absences: '' })); }}
                  placeholder="0"
                  className={addErrors.absences ? 'error' : ''}
                />
                <div className="field-hint">0.5 = half day | 1 = full day | Max: 30 days</div>
                {addErrors.absences && <div className="field-error">{addErrors.absences}</div>}
              </div>
              <div className="modal-field">
                <label><FiGift size={16} style={{ marginRight: 8 }} /> Bonus (EGP)</label>
                <input 
                  type="number" 
                  value={addForm.bonus} 
                  onChange={e => { setAddForm({ ...addForm, bonus: e.target.value }); setAddErrors(prev => ({ ...prev, bonus: '' })); }}
                  placeholder="Optional bonus amount"
                  className={addErrors.bonus ? 'error' : ''}
                />
                {addErrors.bonus && <div className="field-error">{addErrors.bonus}</div>}
              </div>
              <div className="modal-field">
                <label><FiMinus size={16} style={{ marginRight: 8 }} /> Manual Deduction (EGP)</label>
                <input type="number" value={addForm.manualDeduction} onChange={e => setAddForm({ ...addForm, manualDeduction: e.target.value })} placeholder="Optional deduction amount" />
              </div>
              <div className="modal-field">
                <label><FiFileText size={16} style={{ marginRight: 8 }} /> Notes (Optional)</label>
                <textarea rows={2} value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} />
              </div>

              <SalaryPreview preview={addPreview} />
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="modal-save" onClick={handleAdd} disabled={adding}>
                {adding ? "Adding..." : "Add Employee"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Payroll Modal ── */}
      {showEditModal && editingPayroll && editForm && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Payroll — {editingPayroll.displayName}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: "flex", gap: 12 }}>
                <div className="modal-field" style={{ flex: 1 }}>
                  <label><FiCalendar size={16} style={{ marginRight: 8 }} /> Month</label>
                  <select 
                    value={editForm.month} 
                    onChange={e => { setEditForm({ ...editForm, month: Number(e.target.value) }); setEditErrors(prev => ({ ...prev, month: '' })); }}
                    className={editErrors.month ? 'error' : ''}
                  >
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  {editErrors.month && <div className="field-error">{editErrors.month}</div>}
                </div>
                <div className="modal-field" style={{ flex: 1 }}>
                  <label><FiCalendar size={16} style={{ marginRight: 8 }} /> Year</label>
                  <input 
                    type="number" 
                    value={editForm.year} 
                    onChange={e => { setEditForm({ ...editForm, year: e.target.value }); setEditErrors(prev => ({ ...prev, year: '' })); }}
                    className={editErrors.year ? 'error' : ''}
                  />
                  {editErrors.year && <div className="field-error">{editErrors.year}</div>}
                </div>
              </div>
              <div className="modal-field">
                <label><FiDollarSign size={16} style={{ marginRight: 8 }} /> Base Salary (EGP)</label>
                <input 
                  type="number" 
                  value={editForm.basicSalary} 
                  onChange={e => { setEditForm({ ...editForm, basicSalary: e.target.value }); setEditErrors(prev => ({ ...prev, basicSalary: '' })); }}
                  className={editErrors.basicSalary ? 'error' : ''}
                />
                {editErrors.basicSalary && <div className="field-error">{editErrors.basicSalary}</div>}
              </div>
              <div className="modal-field">
                <label><FiTrendingDown size={16} style={{ marginRight: 8 }} /> Absences (days)</label>
                <input 
                  type="number" 
                  step="0.5" 
                  min="0" 
                  max="30" 
                  value={editForm.absences} 
                  onChange={e => { setEditForm({ ...editForm, absences: e.target.value }); setEditErrors(prev => ({ ...prev, absences: '' })); }}
                  className={editErrors.absences ? 'error' : ''}
                />
                <div className="field-hint">0.5 = half day | 1 = full day | Max: 30 days</div>
                {editErrors.absences && <div className="field-error">{editErrors.absences}</div>}
              </div>
              <div className="modal-field">
                <label><FiGift size={16} style={{ marginRight: 8 }} /> Bonus (EGP)</label>
                <input 
                  type="number" 
                  placeholder="0" 
                  value={editForm.bonus} 
                  onChange={e => { setEditForm({ ...editForm, bonus: e.target.value }); setEditErrors(prev => ({ ...prev, bonus: '' })); }}
                  className={editErrors.bonus ? 'error' : ''}
                />
                {editErrors.bonus && <div className="field-error">{editErrors.bonus}</div>}
              </div>
              <div className="modal-field">
                <label><FiMinus size={16} style={{ marginRight: 8 }} /> Manual Deduction (EGP)</label>
                <input type="number" placeholder="0" value={editForm.manualDeduction} onChange={e => setEditForm({ ...editForm, manualDeduction: e.target.value })} />
              </div>
              <div className="modal-field">
                <label><FiFileText size={16} style={{ marginRight: 8 }} /> Notes (Optional)</label>
                <textarea rows={2} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>

              <SalaryPreview preview={editPreview} />
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="modal-save" onClick={handleUpdate} disabled={editing}>
                {editing ? "Updating..." : "Update Payroll"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Employee Detail Modal ── */}
      {showDetailModal && selectedPayroll && (
        <div className="detail-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="detail-modal" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div className="detail-modal-employee">
                <div className="detail-modal-avatar">{selectedPayroll.displayAvatar}</div>
                <div>
                  <div className="detail-modal-name">{selectedPayroll.displayName}</div>
                  <div className="detail-modal-role">{selectedPayroll.displayRole}</div>
                </div>
              </div>
              <button className="detail-modal-close" onClick={() => setShowDetailModal(false)}>✕</button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-modal-grid">
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Base Salary</div>
                  <div className="detail-modal-value">{selectedPayroll.basicSalary.toLocaleString()} EGP</div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Daily Rate</div>
                  <div className="detail-modal-value">{selectedPayroll.dailyRate.toLocaleString()} EGP</div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Absences</div>
                  <div className="detail-modal-value">{selectedPayroll.absences || 0} days</div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Deductions (total)</div>
                  <div className="detail-modal-value red">
                    {selectedPayroll.totalDeduction > 0 ? "-" + selectedPayroll.totalDeduction.toLocaleString() + " EGP" : "—"}
                  </div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Bonuses</div>
                  <div className="detail-modal-value green">
                    {selectedPayroll.bonus > 0 ? "+" + selectedPayroll.bonus.toLocaleString() + " EGP" : "—"}
                  </div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Net Salary</div>
                  <div className="detail-modal-value net">{selectedPayroll.netSalary.toLocaleString()} EGP</div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Department</div>
                  <div className="detail-modal-value">{selectedPayroll.displayDept}</div>
                </div>
                <div className="detail-modal-item">
                  <div className="detail-modal-label">Month</div>
                  <div className="detail-modal-value">{monthLabel(selectedPayroll.month)} {selectedPayroll.year}</div>
                </div>
              </div>
              {selectedPayroll.notes && (
                <div style={{ marginTop: 14 }}>
                  <div className="detail-modal-label">Notes</div>
                  <div className="detail-modal-value" style={{ fontWeight: 400 }}>{selectedPayroll.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal (real records across months/years) ── */}
      {showHistoryModal && historyPayroll && (
        <div className="detail-modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="detail-modal" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div className="detail-modal-employee">
                <div className="detail-modal-avatar">{historyPayroll.displayAvatar}</div>
                <div>
                  <div className="detail-modal-name">{historyPayroll.displayName}</div>
                  <div className="detail-modal-role">Payroll History</div>
                </div>
              </div>
              <button className="detail-modal-close" onClick={() => setShowHistoryModal(false)}>✕</button>
            </div>
            <div className="detail-modal-body">
              {historyEntries.length === 0 ? (
                <div className="history-empty">No records found</div>
              ) : (
                <div className="history-list">
                  {historyEntries.map(h => (
                    <div key={h._id} className="history-item">
                      <div className="history-date">{monthLabel(h.month)} {h.year}</div>
                      <div className="history-details">
                        <div className="history-detail">
                          <span className="history-label">BASE SALARY</span>
                          <span className="history-value">{h.basicSalary.toLocaleString()} EGP</span>
                        </div>
                        <div className="history-detail">
                          <span className="history-label">BONUS</span>
                          <span className="history-value green">
                            {h.bonus > 0 ? "+" + h.bonus.toLocaleString() + " EGP" : "0 EGP"}
                          </span>
                        </div>
                        <div className="history-detail">
                          <span className="history-label">DEDUCTION</span>
                          <span className="history-value red">
                            {h.totalDeduction > 0 ? "-" + h.totalDeduction.toLocaleString() + " EGP" : "0 EGP"}
                          </span>
                        </div>
                        <div className="history-detail">
                          <span className="history-label">NET</span>
                          <span className="history-value net">{h.netSalary.toLocaleString()} EGP</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}

// Small live-preview block shared by the Add and Edit modals.
function SalaryPreview({ preview }) {
  return (
    <div className="salary-preview">
      <div className="salary-preview-title">Live preview</div>
      <div className="salary-preview-grid">
        <div><span>Daily rate</span><b>{preview.dailyRate.toLocaleString()} EGP</b></div>
        <div><span>Absence deduction</span><b className="red">-{preview.absenceDeduction.toLocaleString()} EGP</b></div>
        <div><span>Total deduction</span><b className="red">-{preview.totalDeduction.toLocaleString()} EGP</b></div>
        <div><span>Net salary</span><b className="net">{preview.netSalary.toLocaleString()} EGP</b></div>
      </div>
    </div>
  );
}